import { Link, useOutletContext } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import { leagueDate } from '../../../lib/when';
import type { LeagueContext } from '../LeagueWorkspace';

/**
 * A list of sessions, each linking to its own run of show.
 *
 * The list itself is at `/director/sessions/:id/run-of-show` and is built at 390px, because she
 * works through it standing up in a room that is filling with people. This tab exists so she can
 * find it from a desk beforehand; it is not where the work happens.
 */
export function RunOfShowTab() {
  const { league, sessions } = useOutletContext<LeagueContext>();
  const now = Date.now();

  return (
    <section className="flex flex-col gap-3">
      <p className="max-w-[46rem] text-[14px] leading-relaxed text-ink-2">
        What to do before doors open, during the session, and before you leave. HQ writes it and it is
        the same list everywhere &mdash; open it on your phone when you get there. Nothing on it
        blocks anything; it is a memory aid, not a form.
      </p>

      {sessions.length === 0 ? (
        /*
          A league always gets its sessions when it is created, so this should not happen — which is
          exactly why it is here. An empty bordered box reads as something broken; a sentence reads
          as a state, and points at the one screen that can fix it.
        */
        <p className="rounded-md border border-dashed border-light px-5 py-10 text-center text-[14px] text-ink-3">
          This league has no sessions yet. Add them on the Settings tab.
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {sessions.map((session) => {
            const past = new Date(session.startsAt).getTime() < now;

            return (
              <li
                key={session.id}
                className="flex flex-wrap items-center justify-between gap-4 px-5 py-4"
              >
                <div className="flex items-baseline gap-3">
                  <span className="tabular text-[13px] text-ink-3">
                    {session.sessionNumber}
                  </span>
                  <span className={['font-medium', past ? 'text-ink-2' : ''].join(' ')}>
                    {leagueDate(session.startsAt, league.timeZone, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>
                <Link
                  to={`/director/sessions/${session.id}/run-of-show`}
                  className={`text-[13px] ${LINK_CLASS}`}
                >
                  Open the list
                </Link>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
